import { ArrowTopRightOnSquareIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, InformationCircleIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
    ActionIcon,
    Avatar,
    Badge,
    Box,
    Button,
    Center,
    Checkbox,
    Collapse,
    Divider,
    Group,
    Loader,
    Pagination,
    RingProgress,
    Slider,
    Stack,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { ROUTES } from '../../../constants/routes';
import {
    CandidateAvailabilityStatus,
    CandidateFitResult,
    CandidateStatus,
} from '../../../models/opportunity.model';
import { useCandidateFitQuery } from '../hooks/useCandidateFitQuery';
import { useUpsertShortlistEntry } from '../hooks/useShortlistMutations';
import { useOpportunityShortlistQuery } from '../hooks/useShortlistQuery';

// ── Helpers ───────────────────────────────────────────────────────────────────

const getAvatarSrc = (thumbnail: string | null): string | undefined => {
    if (!thumbnail) return undefined;
    if (thumbnail.startsWith('data:') || thumbnail.startsWith('http')) return thumbnail;
    if (/^[A-Za-z0-9+/=]+$/.test(thumbnail)) return `data:image/jpeg;base64,${thumbnail}`;
    return undefined;
};

const AVAIL_COLOR: Record<CandidateAvailabilityStatus, string> = {
    [CandidateAvailabilityStatus.Available]: 'sopraGreen',
    [CandidateAvailabilityStatus.AvailableSoon]: 'sopraOrange',
    [CandidateAvailabilityStatus.Unavailable]: 'red',
    [CandidateAvailabilityStatus.Unknown]: 'materialGray',
};

const AVAIL_LABEL: Record<CandidateAvailabilityStatus, string> = {
    [CandidateAvailabilityStatus.Available]: 'Available',
    [CandidateAvailabilityStatus.AvailableSoon]: 'Soon',
    [CandidateAvailabilityStatus.Unavailable]: 'Unavailable',
    [CandidateAvailabilityStatus.Unknown]: 'Unknown',
};

const fitScoreColor = (score: number): string => {
    if (score >= 80) return 'sopraGreen';
    if (score >= 55) return 'sopraOrange';
    return 'red';
};

// ── Expandable detail row ─────────────────────────────────────────────────────

const DetailRow = ({ candidate }: { candidate: CandidateFitResult }) => {
    const [open, { toggle }] = useDisclosure(false);
    const w = candidate.appliedWeights;

    return (
        <>
            <Group gap={4} mt={4}>
                <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="dimmed"
                    onClick={toggle}
                    aria-label="Toggle scoring details"
                >
                    {open
                        ? <ChevronUpIcon style={{ width: 12 }} />
                        : <ChevronDownIcon style={{ width: 12 }} />}
                </ActionIcon>
                <Text size="xs" c="dimmed" style={{ cursor: 'pointer' }} onClick={toggle}>
                    Score details
                </Text>
            </Group>

            <Collapse in={open}>
                <Box mt={6} p={8} style={{ background: 'var(--mantine-color-default-border)', borderRadius: 6, opacity: 0.8 }}>
                    <Stack gap={4}>
                        {w.techWeightPercent > 0 && (
                            <Group gap={6}>
                                <Text size="xs" c="dimmed" w={90}>Tech</Text>
                                <Text size="xs" fw={600}>{candidate.techScorePercent ?? '—'}%</Text>
                                <Text size="xs" c="dimmed">
                                    ({candidate.matchedRequiredTechnologies.length} matched
                                    {candidate.missingRequiredTechnologies.length > 0
                                        ? `, ${candidate.missingRequiredTechnologies.length} missing`
                                        : ''})
                                    · weight {w.techWeightPercent}%
                                </Text>
                            </Group>
                        )}
                        {w.languageWeightPercent > 0 && (
                            <Group gap={6}>
                                <Text size="xs" c="dimmed" w={90}>Language</Text>
                                <Text size="xs" fw={600}>{candidate.langScorePercent ?? '—'}%</Text>
                                <Text size="xs" c="dimmed">
                                    ({candidate.matchedRequiredLanguages.length} met
                                    {candidate.missingRequiredLanguages.length > 0
                                        ? `, ${candidate.missingRequiredLanguages.length} missing`
                                        : ''})
                                    · weight {w.languageWeightPercent}%
                                </Text>
                            </Group>
                        )}
                        {w.certificationWeightPercent > 0 && (
                            <Group gap={6}>
                                <Text size="xs" c="dimmed" w={90}>Certification</Text>
                                <Text size="xs" fw={600}>{candidate.certScorePercent ?? '—'}%</Text>
                                <Text size="xs" c="dimmed">· weight {w.certificationWeightPercent}%</Text>
                            </Group>
                        )}
                        <Group gap={6}>
                            <Text size="xs" c="dimmed" w={90}>Availability</Text>
                            <Text size="xs" fw={600}>{candidate.availabilityScorePercent}%</Text>
                            <Text size="xs" c="dimmed">· weight {w.availabilityWeightPercent}%</Text>
                        </Group>
                        {candidate.certificationGap && (
                            <Box mt={4} p={6} style={{ background: 'var(--mantine-color-sopraOrange-1)', borderRadius: 4 }}>
                                <Text size="xs" c="sopraOrange">
                                    <InformationCircleIcon style={{ width: 12, display: 'inline', marginRight: 4 }} />
                                    {candidate.certificationGap.suggestion}
                                </Text>
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Collapse>
        </>
    );
};

// ── Candidate card ────────────────────────────────────────────────────────────

interface CandidateCardProps {
    candidate: CandidateFitResult;
    opportunityId: number;
    shortlisted: boolean;
    shortlistPending: boolean;
    onShortlist: (candidate: CandidateFitResult) => void;
    onOpenPreview: (personId: number) => void;
}

const CandidateCard = ({ candidate, shortlisted, shortlistPending, onShortlist, onOpenPreview }: CandidateCardProps) => {
    const score = candidate.fitScore;
    const color = fitScoreColor(score);

    return (
        <Box
            p="sm"
            style={{
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: 8,
                borderLeft: `3px solid var(--mantine-color-${color}-5)`,
            }}
        >
            <Group align="flex-start" gap="sm" wrap="nowrap">
                <UnstyledButton
                    onClick={() => onOpenPreview(candidate.personId)}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1, minWidth: 0 }}
                >
                    <RingProgress
                        size={52}
                        thickness={4}
                        sections={[{ value: score, color: `${color}.5` }]}
                        label={
                            <Text ta="center" size="xs" fw={700}>{score}</Text>
                        }
                    />

                    <Avatar src={getAvatarSrc(candidate.thumbnailBase64)} radius="xl" size={40} />

                    <Box style={{ flex: 1, minWidth: 0 }}>
                        <Group gap={6} align="center">
                            <Text fw={600} size="sm" truncate style={{ color: 'var(--mantine-color-anchor)' }}>
                                {candidate.firstName} {candidate.lastName}
                            </Text>
                            <Badge
                                size="xs"
                                color={AVAIL_COLOR[candidate.availabilityStatus]}
                                variant="light"
                            >
                                {AVAIL_LABEL[candidate.availabilityStatus]}
                            </Badge>
                            {candidate.officeLocation && (
                                <Text size="xs" c="dimmed">{candidate.officeLocation}</Text>
                            )}
                        </Group>

                        <Group gap={4} mt={4} wrap="wrap">
                            {candidate.matchedRequiredTechnologies.map((t) => (
                                <Tooltip
                                    key={t.canonicalId}
                                    label={t.matchedViaNames.length > 0 ? `via: ${t.matchedViaNames.join(', ')}` : t.canonicalName}
                                    withArrow
                                    disabled={t.matchedViaNames.length === 0}
                                >
                                    <Badge size="xs" variant="light" color="sopraBlue">
                                        {t.canonicalName}
                                        {t.matchedViaNames.length > 0 && (
                                            <Text span size="xs" c="dimmed" ml={2}>→ {t.matchedViaNames.join(', ')}</Text>
                                        )}
                                    </Badge>
                                </Tooltip>
                            ))}
                            {candidate.missingRequiredTechnologies.map((t) => (
                                <Badge key={t.canonicalId} size="xs" variant="outline" color="materialGray">
                                    {t.canonicalName}
                                </Badge>
                            ))}
                            {candidate.matchedRequiredLanguages.map((l) => (
                                <Badge key={l.languageId} size="xs" variant="light" color="sopraGreen">
                                    {l.languageName}
                                </Badge>
                            ))}
                            {candidate.matchedCertifications.map((c, i) => (
                                <Badge key={c.certificateId ?? `fam-${i}`} size="xs" variant="light" color="sopraPurple">
                                    <CheckIcon style={{ width: 8, marginRight: 2 }} />
                                    {c.certificateName ?? c.familyName}
                                </Badge>
                            ))}
                        </Group>

                        <div onClick={(e) => e.stopPropagation()}>
                            <DetailRow candidate={candidate} />
                        </div>
                    </Box>
                </UnstyledButton>

                <Box style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <Stack gap={4}>
                        <Button
                            variant={shortlisted ? 'filled' : 'light'}
                            color="brand.5"
                            size="xs"
                            leftSection={shortlisted
                                ? <StarIconSolid style={{ width: 13, height: 13 }} />
                                : <StarIcon style={{ width: 13, height: 13 }} />
                            }
                            loading={shortlistPending}
                            disabled={shortlisted}
                            onClick={() => onShortlist(candidate)}
                            aria-label={shortlisted ? 'Already shortlisted' : 'Add to shortlist'}
                        >
                            {shortlisted ? 'Shortlisted' : 'Shortlist'}
                        </Button>
                        <Button
                            component="a"
                            href={ROUTES.CV_DETAILS(candidate.personId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="subtle"
                            color="brand.5"
                            size="xs"
                            leftSection={<ArrowTopRightOnSquareIcon style={{ width: 13, height: 13 }} />}
                            aria-label="Open full profile"
                        >
                            Full profile
                        </Button>
                    </Stack>
                </Box>
            </Group>
        </Box>
    );
};

// ── Main tab ──────────────────────────────────────────────────────────────────

interface PositionBestFitTabProps {
    opportunityId: number;
    positionId: number;
    onOpenPreview: (personId: number) => void;
}

export const PositionBestFitTab = ({ opportunityId, positionId, onOpenPreview }: PositionBestFitTabProps) => {
    const [page, setPage] = useState(1);
    const [minFitScore, setMinFitScore] = useState(0);
    const [includeRejected, setIncludeRejected] = useState(false);
    const pageSize = 10;

    const { data, isLoading, isError, refetch, isFetching } = useCandidateFitQuery(
        opportunityId, positionId, page, pageSize, minFitScore, includeRejected,
    );

    const { data: shortlistData } = useOpportunityShortlistQuery(opportunityId, positionId);
    const shortlistedIds = new Set(shortlistData?.map((e) => e.personId) ?? []);

    const { mutate: upsert, isPending: upsertPending } = useUpsertShortlistEntry(opportunityId, positionId);
    const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());

    const handleShortlist = (candidate: CandidateFitResult) => {
        setPendingIds((prev) => new Set(prev).add(candidate.personId));
        upsert(
            { personId: candidate.personId, status: CandidateStatus.Shortlisted, notes: null, fitScore: candidate.fitScore },
            {
                onSuccess: () => {
                    notifications.show({
                        title: 'Shortlisted',
                        message: `${candidate.firstName} ${candidate.lastName} added to shortlist.`,
                        color: 'sopraGreen',
                    });
                },
                onError: () => {
                    notifications.show({ title: 'Error', message: 'Could not add to shortlist.', color: 'red' });
                },
                onSettled: () => {
                    setPendingIds((prev) => {
                        const next = new Set(prev);
                        next.delete(candidate.personId);
                        return next;
                    });
                },
            },
        );
    };

    return (
        <Stack gap="sm" pt="sm">
            {/* Controls */}
            <Group gap="md" align="flex-end" wrap="wrap">
                <Box style={{ flex: 1, minWidth: 180 }}>
                    <Text size="xs" fw={500} mb={4}>Min. fit score: {minFitScore > 0 ? minFitScore : 'Any'}</Text>
                    <Slider
                        min={0}
                        max={100}
                        step={5}
                        value={minFitScore}
                        onChange={(v) => { setMinFitScore(v); setPage(1); }}
                        marks={[{ value: 0 }, { value: 50 }, { value: 100 }]}
                        size="xs"
                        aria-label="Minimum fit score filter"
                    />
                </Box>
                <Checkbox
                    label="Include rejected"
                    checked={includeRejected}
                    onChange={(e) => { setIncludeRejected(e.currentTarget.checked); setPage(1); }}
                    size="xs"
                />
                <Button
                    size="xs"
                    variant="light"
                    onClick={() => refetch()}
                    loading={isFetching}
                    aria-label="Refresh scoring results"
                >
                    Rescore
                </Button>
            </Group>

            <Divider />

            {/* Results */}
            {isLoading ? (
                <Center py="xl"><Loader size="sm" /></Center>
            ) : isError ? (
                <Center py="xl">
                    <Stack align="center" gap="xs">
                        <Text c="dimmed" size="sm">Could not load candidates.</Text>
                        <Button size="xs" variant="light" onClick={() => refetch()}>Retry</Button>
                    </Stack>
                </Center>
            ) : !data || data.totalCount === 0 ? (
                <Center py="xl">
                    <Text c="dimmed" size="sm">
                        {minFitScore > 0
                            ? `No candidates scored ≥ ${minFitScore}. Try lowering the filter.`
                            : 'No candidates found for this position\'s requirements.'}
                    </Text>
                </Center>
            ) : (
                <>
                    <Text size="xs" c="dimmed">
                        {data.totalCount} candidate{data.totalCount !== 1 ? 's' : ''} scored
                        {minFitScore > 0 ? ` (min ${minFitScore})` : ''}
                        {isFetching && <Loader size={10} ml={6} />}
                    </Text>

                    <Stack gap="xs">
                        {data.items.map((candidate) => (
                            <CandidateCard
                                key={candidate.personId}
                                candidate={candidate}
                                opportunityId={opportunityId}
                                shortlisted={shortlistedIds.has(candidate.personId)}
                                shortlistPending={pendingIds.has(candidate.personId) || upsertPending}
                                onShortlist={handleShortlist}
                                onOpenPreview={onOpenPreview}
                            />
                        ))}
                    </Stack>

                    {data.totalPages > 1 && (
                        <Group justify="center" mt="xs">
                            <Pagination
                                value={page}
                                onChange={setPage}
                                total={data.totalPages}
                                size="sm"
                            />
                        </Group>
                    )}
                </>
            )}
        </Stack>
    );
};
